import {
  ConsolidationProgress,
  InboundChange,
} from "@/models/legislation/metadata/ConsolidationMetadata";
import {
  getInboundProgress,
  getLastVerifiedConsolidationDate,
} from "@/lib/consolidationMetadata";
import {
  countConsolidationAmendmentsAfter,
  resolveAnchorAccessionOnTimeline,
  scopeInboundChangesAfterAnchorOnTimeline,
} from "@/lib/timelineUniverseParams";
import { flattenTimelineItems } from "@/lib/consolidationViewerUtils";
import { TimelineItem } from "@/models/legislation/Timeline";
import { dateValue } from "@/utils/legislationMetadataUtils";
import { isConsolidationEligibleDocumentType } from "@/utils/legislationDocumentTypeUtils";

export type ConsolidationProgressVariant =
  | "complete"
  | "partial"
  | "none"
  | "missing";

export type ConsolidationProgressDisplay = {
  label: string;
  variant: ConsolidationProgressVariant;
  progress: ConsolidationProgress;
  lastVerifiedAt: string | null;
  lastVerifiedLabel: string | null;
  tooltip: string;
};

export const CONSOLIDATION_PROGRESS_VARIANT_CLASS: Record<
  ConsolidationProgressVariant,
  string
> = {
  complete: "text-success bg-success/10 border-success/20",
  partial: "text-warning bg-warning/10 border-warning/30",
  none: "text-muted-foreground bg-muted/40 border-border",
  missing: "text-destructive bg-destructive/10 border-destructive/20",
};

export const resolveConsolidationProgressVariant = (
  progress: ConsolidationProgress
): Exclude<ConsolidationProgressVariant, "missing"> => {
  if (progress.total === 0) return "none";
  if (progress.approved === progress.total) return "complete";
  if (progress.approved === 0 && progress.needReview > 0) return "partial";
  if (progress.approved === 0) return "none";
  return "partial";
};

export const formatLastVerifiedConsolidationLabel = (
  approvedAt: string | null
): string | null => {
  if (!approvedAt) return null;
  const formatted = dateValue(approvedAt);
  if (!formatted) return null;
  return `Senest godkendt ${formatted}`;
};

const formatVerifiedProgressTooltip = (
  approved: number,
  total: number
): string => `${approved} af ${total} konsolideringer verificeret`;

/**
 * Denominator for the verified/total badge: the structural amendment count from the
 * timeline (timeline_gold, excludes type 90). Decoupled from the inbound list length —
 * the numerator comes from live status, the denominator only from timeline structure.
 */
export const resolveConsolidationProgressTotal = (
  timelineAmendmentCountAfter: number
): number => Math.max(timelineAmendmentCountAfter, 0);

const buildConsolidationProgressDisplay = (
  inbound: ReadonlyArray<InboundChange>,
  timelineAmendmentCountAfter: number
): ConsolidationProgressDisplay => {
  const inboundProgress = getInboundProgress(inbound);
  const total = resolveConsolidationProgressTotal(timelineAmendmentCountAfter);
  // Numerator is live; clamp to the structural denominator so drift can never render
  // e.g. "3/2". needReview fills only the remaining slots after approved.
  const approved = Math.min(inboundProgress.approved, total);
  const needReview = Math.min(inboundProgress.needReview, total - approved);
  const progress: ConsolidationProgress = { approved, needReview, total };
  const variant = resolveConsolidationProgressVariant(progress);
  const lastVerifiedAt = getLastVerifiedConsolidationDate(inbound);
  const lastVerifiedLabel =
    formatLastVerifiedConsolidationLabel(lastVerifiedAt);

  return {
    label: `${progress.approved}/${progress.total} verificeret`,
    variant,
    progress,
    lastVerifiedAt,
    lastVerifiedLabel,
    tooltip: formatVerifiedProgressTooltip(progress.approved, progress.total),
  };
};

export type ConsolidationProgressDisplayInput = {
  inboundChanges: ReadonlyArray<InboundChange> | undefined;
  timelineAmendmentCountAfter: number | undefined;
  documentTypeId: string | number | undefined | null;
};

export type ConsolidationProgressDisplayOptions = {
  timelineItems?: TimelineItem[];
  anchorAccession?: string;
  documentId?: string;
};

/** Shared consolidation status for legislation table and document timeline. */
export const getConsolidationProgressDisplay = (
  {
    inboundChanges,
    timelineAmendmentCountAfter,
    documentTypeId,
  }: ConsolidationProgressDisplayInput,
  options?: ConsolidationProgressDisplayOptions
): ConsolidationProgressDisplay | null => {
  const resolvedAnchor = resolveAnchorAccessionOnTimeline(
    options?.timelineItems,
    options?.anchorAccession,
    options?.documentId
  );
  const amendmentCountFromTimeline =
    options?.timelineItems && resolvedAnchor
      ? countConsolidationAmendmentsAfter(
          options.timelineItems,
          resolvedAnchor,
          documentTypeId
        )
      : 0;

  const anchorOnTimeline = Boolean(
    options?.timelineItems &&
      resolvedAnchor &&
      flattenTimelineItems(options.timelineItems).some(
        (item) => item.accessionNumber === resolvedAnchor
      )
  );

  const scopedInbound =
    anchorOnTimeline && resolvedAnchor
      ? scopeInboundChangesAfterAnchorOnTimeline(
          options!.timelineItems,
          resolvedAnchor,
          inboundChanges,
          { consolidationScope: true, documentTypeId }
        )
      : (inboundChanges ?? []);

  const amendmentCount =
    anchorOnTimeline && options?.timelineItems
      ? amendmentCountFromTimeline
      : amendmentCountFromTimeline > 0
        ? amendmentCountFromTimeline
        : (timelineAmendmentCountAfter ?? 0);

  if (!isConsolidationEligibleDocumentType(documentTypeId) || amendmentCount <= 0)
    return null;

  if (!scopedInbound.length) {
    return {
      label: "Ingen konsolideringer",
      variant: "missing",
      progress: {
        approved: 0,
        needReview: 0,
        total: amendmentCount,
      },
      lastVerifiedAt: null,
      lastVerifiedLabel: null,
      tooltip: "Ingen konsolideringer",
    };
  }

  return buildConsolidationProgressDisplay(scopedInbound, amendmentCount);
};
